import {
  ComponentPropsWithoutRef,
  ElementType,
  PropsWithChildren,
  useState,
} from 'react';

import { twMerge } from 'tailwind-merge';

import { cn } from '../lib';
import { Soon } from './components/soon';
import { IconWrapper } from './icon-wrapper';
import { Switch } from './switch';
import { Typography } from './typography';

interface ModalCardProps {
  className?: string;
}
interface ModalCardItemBaseProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  hasSwitch?: boolean;
  onSwitch?: VoidFunction;
  isChecked?: boolean;
  isSoon?: boolean;
  rightContent?: React.ReactNode;
  noWrapper?: boolean;
}
type ModalCardItemWithIconProps<T extends ElementType = 'div'> =
  ModalCardItemBaseProps & {
    as?: T;
  } & Omit<ComponentPropsWithoutRef<T>, keyof ModalCardItemBaseProps>;

export function ModalCardItemWithIcon<T extends ElementType = 'div'>({
  title,
  description,
  icon,
  onSwitch,
  isChecked,
  className,
  hasSwitch = false,
  as,
  isSoon,
  rightContent, //TODO: refactor soon and switch inside rightContent
  noWrapper,
  ...props
}: ModalCardItemWithIconProps<T>) {
  const Component = as ?? 'div';
  const [switchChecked, setSwitchChecked] = useState(isChecked || false);
  const handleToggle = () => {
    setSwitchChecked((prev) => !prev);
    onSwitch?.();
  };
  return (
    <Component className={cn('flex w-full py-2', className)} {...props}>
      {noWrapper ? icon : <IconWrapper>{icon}</IconWrapper>}
      <div className="flex flex-1 flex-col justify-center gap-y-1 pl-4 pr-2">
        <Typography className={twMerge(isSoon && 'text-base-100')}>
          {title}
        </Typography>
        {description && (
          <Typography variant="14-r" color="base-300">
            {description}
          </Typography>
        )}
      </div>
      {hasSwitch && (
        <div onClick={handleToggle} className="-m-5 h-fit cursor-pointer p-5">
          <Switch checked={switchChecked} />
        </div>
      )}
      {isSoon && <Soon className="bg-base-100" />}
      {rightContent}
    </Component>
  );
}
export const ModalCard = ({
  children,
  className,
}: PropsWithChildren<ModalCardProps>) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-y-3 rounded-[28px] bg-base-300 px-5 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
};
